import { Suspense, useMemo, useRef } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { CatchBoundary, useNavigate } from '@tanstack/react-router'
import { atom, useAtom, useSetAtom } from 'jotai'

import { Skeleton, Stack } from '@forto-dev/phoenix-ui-core'
import {
  Avatar,
  Button,
  Cluster,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Indicator,
  Separator,
  Spinner,
} from '@forto-dev/phoenix-ui-core/common'
import { Notifications, NotificationsList } from '@forto-dev/phoenix-ui-core/ship'
import { useLocalisation } from '@forto-dev/platform-localisation'

import { createErrorFallback } from '@/fragments/create-error-fallback'
import { useLoadMoreOnScroll } from '@/utils/hooks/useLoadMoreOnScroll'

import { notificationsCountQueryOptions } from './api'
import { useUpdateNotificationStatus } from './api/useUpdateNotificationStatus'
import { NotificationPaginationContextProvider, NotificationSortContextProvider, useNotifications } from './hooks'
import { NotificationItem } from './list/notificationItem'

const notificationMenuOpenAtom = atom(false)
const NotificationsInWidget = () => {
  const updateStatusMutation = useUpdateNotificationStatus()
  const setIsOpen = useSetAtom(notificationMenuOpenAtom)

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isFetching, items: notifications } = useNotifications()
  const { translate } = useLocalisation()
  const newAndUnreadIDs = useMemo(
    () => notifications.filter((notification) => notification.status !== 'read').map((d) => d.id),
    [notifications]
  )
  const disableMarkAllAsRead = isFetchingNextPage || isFetching || newAndUnreadIDs.length === 0
  const observedButtonRef = useRef<HTMLButtonElement | null>(null)
  useLoadMoreOnScroll(observedButtonRef, fetchNextPage, hasNextPage, isFetchingNextPage)
  return (
    <>
      <DropdownMenuItem
        onClick={() => {
          if (newAndUnreadIDs.length) {
            updateStatusMutation.mutate({
              data: {
                ids: newAndUnreadIDs,
                status: 'read',
              },
            })
          }
        }}
        disabled={disableMarkAllAsRead}
        asChild
      >
        <Button variant="ghost" size="sm" disabled={disableMarkAllAsRead}>
          {translate('cockpit.notifications.markAllAsRead')}
        </Button>
      </DropdownMenuItem>
      <Separator orientation="horizontal" />
      <Notifications>
        <NotificationsList nested>
          {notifications.map((notification) => {
            return (
              <NotificationItem notification={notification} key={notification.id} onClick={() => setIsOpen(false)} />
            )
          })}
          {hasNextPage ? (
            <Stack x="center" gap={24}>
              <Button ref={observedButtonRef} variant="ghost">
                {isFetchingNextPage ? (
                  <Cluster gap={8} y="center">
                    <Spinner size="xs" />
                  </Cluster>
                ) : (
                  '…'
                )}
              </Button>
            </Stack>
          ) : null}
        </NotificationsList>
      </Notifications>
    </>
  )
}

const LeanNotificationWidget = () => {
  const { data: notificationsCount } = useSuspenseQuery(notificationsCountQueryOptions())
  const { translate } = useLocalisation()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useAtom(notificationMenuOpenAtom)

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger>
        <Indicator
          value={notificationsCount.new + notificationsCount.unread || false}
          data-testid="cockpit-notifications-button"
        >
          <Avatar>
            <Icon name="Notification" />
          </Avatar>
        </Indicator>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* w-72 is to match the sidebar size from the notification-center  */}
        {/* TODO(Masood): remove once a better solution is added */}
        {/* eslint-disable-next-line react/forbid-dom-props */}
        <div className="w-72">
          <Suspense fallback={<Skeleton variant="card" />}>
            <NotificationsInWidget />
          </Suspense>
        </div>
        <Separator orientation="horizontal" />
        <DropdownMenuItem
          onClick={() => {
            navigate({
              to: '/notification-center',
            })
          }}
          asChild
        >
          <Button variant="ghost" size="sm">
            {translate('cockpit.notifications.all')}
          </Button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const NotificationWidget = () => {
  return (
    <CatchBoundary
      getResetKey={() => 1}
      errorComponent={createErrorFallback('An error occurred while loading notifications')}
    >
      <Suspense fallback={<Skeleton variant="avatar" />}>
        <NotificationPaginationContextProvider>
          <NotificationSortContextProvider>
            <LeanNotificationWidget />
          </NotificationSortContextProvider>
        </NotificationPaginationContextProvider>
      </Suspense>
    </CatchBoundary>
  )
}
